import { useCallback, useEffect, useState } from 'react';
import type { PaginationMetaI } from 'types';
import useDebounce from 'Hooks/useDebounce';
import usePageSize from 'Hooks/usePageSize';
import courseMembersApi, { CourseMemberI, CourseMembersMetaI, MembershipTab } from 'Api/axios/course-members-api';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type FilterKey = 'departmentId' | 'jobTitleId' | 'terminalId';

/** Resultado de un alta: si el backend respondió, y a cuántos tocó de verdad. */
export interface AddResult {
  ok: boolean;
  added: number;
  skipped: number;
}

/** Los tres filtros son de selección múltiple; el arreglo vacío es "sin filtro". */
type Filters = Record<FilterKey, string[]>;

const EMPTY_FILTERS: Filters = { departmentId: [], jobTitleId: [], terminalId: [] };

/** El backend recibe los ids separados por coma. Sin selección, no manda el filtro. */
const asParam = (ids: string[]): string | undefined => (ids.length > 0 ? ids.join(',') : undefined);

const EMPTY_META: CourseMembersMetaI = {
  mandatory: 0,
  optional: 0,
  isOptional: false,
  optionalScope: null,
};

/**
 * Estado y operaciones de la pestaña Miembros contra `/api/courses/:id/members`.
 *
 * Búsqueda, filtros, pestaña y paginación son del lado del servidor. La
 * búsqueda se debouncea, y cambiar pestaña, búsqueda o filtro regresa a la
 * primera página **en el mismo evento** —no en un efecto aparte— para que no se
 * disparen dos peticiones por cada interacción.
 *
 * Las tres mutaciones devuelven `true` solo si el backend confirmó. Recargan
 * únicamente en ese caso: redibujar antes de saberlo le mentiría al
 * administrador.
 *
 * @param courseId - UUID del curso en edición.
 */
export const useCourseMembers = (courseId: string) => {
  const [members, setMembers] = useState<CourseMemberI[]>([]);
  const [meta, setMeta] = useState<CourseMembersMetaI>(EMPTY_META);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = usePageSize('courseMembers', DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [tab, setTabRaw] = useState<MembershipTab>('mandatory');
  const [search, setSearchRaw] = useState('');
  const [filters, setFiltersRaw] = useState<Filters>(EMPTY_FILTERS);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const fetchPage = useCallback(
    async (targetPage: number, membership: MembershipTab, query: string, current: Filters) => {
      if (!courseId) return;

      setLoading(true);

      const res = await courseMembersApi.readAll(courseId, {
        membership,
        search: query.trim() || undefined,
        departmentId: asParam(current.departmentId),
        jobTitleId: asParam(current.jobTitleId),
        terminalId: asParam(current.terminalId),
        page: targetPage,
        pageSize,
      });

      setMembers(res.data || []);
      setMeta((res.meta as unknown as CourseMembersMetaI) ?? EMPTY_META);

      const pag = res.pagination ?? null;
      setPagination(pag);
      setLoading(false);

      // Si la página quedó fuera de rango (p. ej. tras borrar el último de la
      // última página), retrocede a la última válida.
      if (pag && pag.totalPages > 0 && targetPage > pag.totalPages) setPage(pag.totalPages);
    },
    [courseId, pageSize]
  );

  useEffect(() => {
    fetchPage(page, tab, debouncedSearch, filters);
  }, [page, pageSize, tab, debouncedSearch, filters, fetchPage]);

  // Cambiar el tamaño vuelve a la primera página: quien estaba en la 7 con 10
  // por página se saldría del rango al pedir 100, y la tabla saldría vacía.
  const setPageSize = (value: number) => {
    setPageSizeState(value);
    setPage(1);
  };

  const setTab = (value: MembershipTab) => {
    setTabRaw(value);
    setPage(1);
  };

  const setSearch = (value: string) => {
    setSearchRaw(value);
    setPage(1);
  };

  const setFilter = (key: FilterKey, value: string[]) => {
    setFiltersRaw(previous => ({ ...previous, [key]: value }));
    setPage(1);
  };

  const refresh = () => fetchPage(page, tab, debouncedSearch, filters);

  /**
   * Da de alta y devuelve lo que el backend dice que pasó, no solo si hubo
   * error. El alta es idempotente: a quien ya es miembro lo cuenta como
   * omitido, y la pantalla necesita saberlo para no cantar un éxito que no
   * añadió a nadie.
   */
  const addMembers = async (userIds: string[], membership: MembershipTab): Promise<AddResult> => {
    const res = await courseMembersApi.add(courseId, userIds, membership);
    if (res.error !== null) return { ok: false, added: 0, skipped: 0 };
    await refresh();
    return { ok: true, added: res.data?.added ?? 0, skipped: res.data?.skipped ?? 0 };
  };

  const removeMembers = async (userIds: string[]): Promise<boolean> => {
    const res = await courseMembersApi.remove(courseId, userIds);
    if (res.error !== null) return false;
    await refresh();
    return true;
  };

  const moveToMandatory = async (userIds: string[]): Promise<boolean> => {
    const res = await courseMembersApi.moveToMandatory(courseId, userIds);
    if (res.error !== null) return false;
    await refresh();
    return true;
  };

  return {
    members,
    meta,
    pagination,
    page,
    goToPage: setPage,
    pageSize,
    setPageSize,
    loading,
    tab,
    setTab,
    search,
    setSearch,
    filters,
    setFilter,
    /** Curso abierto a toda la empresa: la pestaña de opcionales no se edita aquí. */
    readOnlyOptional: meta.isOptional && meta.optionalScope === 'all',
    /** Hay miembros opcionales en un curso que no está marcado como opcional: no tienen acceso. */
    optionalWithoutAccess: !meta.isOptional && meta.optional > 0,
    addMembers,
    removeMembers,
    moveToMandatory,
  };
};

export default useCourseMembers;
